/*
  TaskCard — one task on the board, readable at a glance in three lines:
    1. status icon + title (2-line clamp)            [+ "Blocked: reason" when blocked]
    2. priority flag · due badge · 📎 attachments · 📍 location
    3. project tag · origin chip (stage / material / invoice)            avatars | Unassigned
  Actions appear on hover / keyboard focus (a floating pill): Done · Due date · Assign · ⋯
  (⋯ also has Move to section — the keyboard / touch alternative to dragging — and Delete…).
  On touch screens (no hover) only the ⋯ button shows, inline at the end of line 3.
  The whole card drags; there is no grip taking space.
  Wave 2: `selected` = the task open in the task pane (navy border); `draggable` is off outside the Section
  grouping (dragging means "move to section").
  `readOnly` (BUG-002: Tasks is View only for the role, or a Viewer account): the card only opens the task — no
  action pill, no ⋯ menu, no "Unassigned" assign button, no drag and no swipe.
*/
import { useRef, useState } from 'react';
import {
  CalendarDays,
  CircleCheck,
  CircleDashed,
  CircleDot,
  CircleSlash,
  Flag,
  FolderInput,
  GitBranch,
  MapPin,
  OctagonAlert,
  Package,
  PanelRightOpen,
  Paperclip,
  ReceiptText,
  RotateCcw,
  Trash2,
  TriangleAlert,
  UserPlus,
} from 'lucide-react';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { RowActions } from '@/components/ui/row-actions';
import { StatusPill } from '@/components/ui/status-pill';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/format';
import {
  avatarFill,
  dateInDays,
  dueDatePatch,
  dueInfo,
  initials,
  isClosed,
  normalizeStatus,
  priorityMeta,
  sectionOf,
  statusMeta,
  taskAssignees,
  taskOrigin,
  taskProjects,
} from './taskModel';
import { AssignPanel, DueDatePanel, MovePanel } from './TaskQuickPanels';
import { useQueuedSelection } from './useQueuedSelection';

const STATUS_ICONS = {
  open: CircleDashed,
  progress: CircleDot,
  blocked: OctagonAlert,
  cancelled: CircleSlash,
  done: CircleCheck,
};
const ORIGIN_ICONS = { stage: GitBranch, material: Package, invoicing: ReceiptText };

export function StatusIcon({ status, className }) {
  const meta = statusMeta(status);
  const Icon = STATUS_ICONS[meta.icon] || CircleDashed;
  return (
    <span className={cn('inline-flex shrink-0', className)} title={meta.label}>
      <Icon className={cn('h-[15px] w-[15px]', meta.iconClass)} strokeWidth={2.25} aria-hidden="true" />
      <span className="sr-only">{meta.label}:</span>
    </span>
  );
}

export function PriorityFlag({ priority, className }) {
  const meta = priorityMeta(priority);
  return (
    <span className={cn('inline-flex items-center gap-1 font-bold', meta.textClass, className)}>
      <Flag className="h-[13px] w-[13px]" strokeWidth={2.25} aria-hidden="true" />
      {meta.label}
      <span className="sr-only"> priority</span>
    </span>
  );
}

export function DueBadge({ task, className }) {
  const due = dueInfo(task);
  if (due.kind === 'none') {
    return <span className={cn('font-medium text-ink-3', className)}>No date</span>;
  }
  const title = due.date ? `Due ${formatDate(task.due_date)}` : undefined;
  // A done or cancelled task is never "Overdue" or "Today": just its date.
  if (isClosed(task)) return <StatusPill tone="gray" hideDot label={due.date} size="sm" title={title} className={className} />;
  if (due.kind === 'overdue') {
    return <StatusPill tone="red" icon={TriangleAlert} label={due.label} size="sm" title={title} className={className} data-testid="task-due-overdue" />;
  }
  if (due.kind === 'today') {
    return <StatusPill tone="amber" hideDot label="Today" size="sm" title={title} className={className} />;
  }
  return <StatusPill tone="gray" hideDot label={due.label} size="sm" title={title} className={className} />;
}

export function OriginChip({ task, className }) {
  const origin = taskOrigin(task);
  if (!origin) return null;
  const Icon = ORIGIN_ICONS[origin.kind];
  return (
    <span
      className={cn('inline-flex min-w-0 shrink items-center gap-[3px] text-[11px] font-semibold text-ink-2', className)}
      title={origin.title}
      data-testid="task-origin-chip"
    >
      <Icon className="h-3 w-3 shrink-0 text-brand-ink" aria-hidden="true" />
      <span className="truncate">{origin.short}</span>
    </span>
  );
}

/** A person's initials on their colour — the same colour on cards, the pane, the table and comments. */
export function PersonAvatar({ person, size = 22, className }) {
  return (
    <span
      aria-hidden="true"
      className={cn('inline-grid shrink-0 place-items-center rounded-full font-bold leading-none text-white', className)}
      style={{ width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.43)), background: avatarFill(person?.id || person?.name) }}
    >
      {initials(person?.name)}
    </span>
  );
}

function Avatars({ assignees }) {
  const shown = assignees.slice(0, 3);
  const names = assignees.map((assignee) => assignee.name).join(', ');
  return (
    <span className="flex shrink-0 items-center" title={names}>
      <span className="sr-only">Assigned to {names}</span>
      {shown.map((assignee, index) => (
        <span
          key={assignee.id || assignee.name}
          aria-hidden="true"
          className={cn(
            'grid h-[22px] w-[22px] place-items-center rounded-full text-[9.5px] font-bold text-white',
            index > 0 && '-ml-1.5 ring-2 ring-[color:var(--surface)]'
          )}
          style={{ background: avatarFill(assignee.id || assignee.name) }}
        >
          {initials(assignee.name)}
        </span>
      ))}
      {assignees.length > 3 ? (
        <span aria-hidden="true" className="-ml-1.5 grid h-[22px] min-w-[22px] place-items-center rounded-full bg-[color:var(--status-gray-bg)] px-1 text-[9.5px] font-bold text-ink-2 ring-2 ring-[color:var(--surface)]">
          +{assignees.length - 3}
        </span>
      ) : null}
    </span>
  );
}

const PHONE_QUERY = '(max-width: 639px)';
const SWIPE_COMMIT = 0.32; // share of the card width a swipe must travel to act

const actionButton = 'grid h-[26px] w-[26px] place-items-center rounded-md text-ink-2 transition-colors hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export default function TaskCard({
  task,
  projectNameById,
  members = [],
  sections = [],
  done = false,
  dragging = false,
  selected = false,
  draggable: draggableProp = true,
  readOnly = false,
  onOpen,
  onDone,
  onReopen,
  onPatch,
  onMove,
  onDelete,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}) {
  const draggable = draggableProp && !readOnly;
  const [panel, setPanel] = useState(null); // 'due' | 'assign' | 'move' | null
  const [menuOpen, setMenuOpen] = useState(false);
  const pillRef = useRef(null);
  const returnFocusRef = useRef(null);

  const status = normalizeStatus(task.status);
  const assignees = taskAssignees(task);
  const projects = taskProjects(task, projectNameById);
  const attachments = (task.attachments || []).length;
  const location = (task.locations || [])[0];
  const blocked = status === 'Blocked';

  const openPanel = (next, event) => {
    // Focus goes back to what opened the panel: the action button, or the ⋯ button for menu items.
    returnFocusRef.current = event?.currentTarget || pillRef.current?.querySelector('[aria-haspopup="menu"]');
    setPanel((current) => (current === next ? null : next));
  };

  // Assign people is a multi-select saved on every click: picks are kept locally and saved in order with the
  // whole list each time, so a quick second click during a slow save never drops the first one.
  const knownPeople = new Map([...members, ...assignees].map((item) => [item.id, item]));
  const savedAssigneeIds = assignees.map((assignee) => assignee.id);
  const [assigneeIds, toggleAssigneeId] = useQueuedSelection(
    task.id,
    savedAssigneeIds,
    (nextIds, { adding, context: member }) => onPatch(task, {
      assignee_ids: nextIds,
      assignees: nextIds.map((id) => knownPeople.get(id)).filter(Boolean),
    }, { message: adding ? `“${task.name}” assigned to ${member.name}` : `${member.name} removed from “${task.name}”` }),
  );
  const toggleAssignee = (member) => toggleAssigneeId(member.id, member);
  const shownAssignees = assigneeIds === savedAssigneeIds
    ? assignees
    : assigneeIds.map((id) => knownPeople.get(id)).filter(Boolean); // picks still saving show right away

  const menuItems = done
    ? [
      { label: 'Open details', icon: PanelRightOpen, onSelect: () => onOpen(task) },
      { label: 'Reopen task', icon: RotateCcw, onSelect: () => onReopen(task) },
      { label: 'Delete task…', icon: Trash2, danger: true, onSelect: () => onDelete(task), testId: `task-delete-${task.id}` },
    ]
    : [
      { label: 'Open details', icon: PanelRightOpen, onSelect: () => onOpen(task) },
      { label: 'Mark done', icon: CircleCheck, onSelect: () => onDone(task) },
      { label: 'Set due date…', icon: CalendarDays, onSelect: () => openPanel('due') },
      { label: 'Assign people…', icon: UserPlus, onSelect: () => openPanel('assign') },
      { label: 'Move to section…', icon: FolderInput, onSelect: () => openPanel('move'), testId: `task-move-${task.id}` },
      { label: 'Delete task…', icon: Trash2, danger: true, onSelect: () => onDelete(task), testId: `task-delete-${task.id}` },
    ];

  const handleCardClick = (event) => {
    if (suppressClickRef.current) return;
    if (event.target.closest('button, a, input, [role="menu"], [data-radix-popper-content-wrapper]')) return;
    onOpen(task);
  };

  // Phones: swipe left = Done, swipe right = due tomorrow (both with Undo in the toast).
  const swipeRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [swipeX, setSwipeX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const handlePointerDown = (event) => {
    if (done || readOnly || event.pointerType !== 'touch' || event.target.closest('button, a, input')) return;
    if (typeof window !== 'undefined' && window.matchMedia && !window.matchMedia(PHONE_QUERY).matches) return;
    swipeRef.current = { id: event.pointerId, x0: event.clientX, y0: event.clientY, axis: null, width: event.currentTarget.offsetWidth || 320 };
  };
  const handlePointerMove = (event) => {
    const swipe = swipeRef.current;
    if (!swipe || swipe.id !== event.pointerId) return;
    const dx = event.clientX - swipe.x0;
    const dy = event.clientY - swipe.y0;
    if (!swipe.axis) {
      if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy) * 1.4) {
        swipe.axis = 'x';
        setSwiping(true);
        try {
          event.currentTarget.setPointerCapture?.(event.pointerId);
        } catch {
          // the pointer is already gone (e.g. lifted) — the swipe still works without capture
        }
      } else if (Math.abs(dy) > 10) {
        swipeRef.current = null; // a vertical scroll, not a swipe
        return;
      } else {
        return;
      }
    }
    const limit = swipe.width * 0.6;
    setSwipeX(Math.max(-limit, Math.min(limit, dx)));
  };
  const handlePointerEnd = (event) => {
    const swipe = swipeRef.current;
    swipeRef.current = null;
    if (!swipe || swipe.axis !== 'x') return;
    const dx = event.clientX - swipe.x0;
    setSwiping(false);
    setSwipeX(0);
    suppressClickRef.current = true;
    window.setTimeout(() => { suppressClickRef.current = false; }, 400);
    if (event.type === 'pointercancel') return;
    if (dx <= -swipe.width * SWIPE_COMMIT) onDone(task);
    else if (dx >= swipe.width * SWIPE_COMMIT) {
      onPatch(task, dueDatePatch(task, dateInDays(1)), { message: `“${task.name}” due tomorrow` });
    }
  };

  const pillVisible = Boolean(panel) || menuOpen;

  return (
    <Popover open={Boolean(panel)} onOpenChange={(open) => { if (!open) setPanel(null); }}>
      <div className={cn('relative', swipeX && 'overflow-hidden rounded-xl')}>
        {swipeX ? (
          <div
            aria-hidden="true"
            className={cn(
              'absolute inset-0 flex items-center rounded-xl px-4 text-[13px] font-bold',
              swipeX < 0 ? 'justify-end bg-status-green-ink text-white' : 'justify-start bg-brand text-brand-fg'
            )}
          >
            {swipeX < 0
              ? <span className="inline-flex items-center gap-1.5"><CircleCheck className="h-4 w-4" />Done</span>
              : <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4" />Tomorrow</span>}
          </div>
        ) : null}
        <PopoverAnchor asChild>
          <article
            draggable={!done && draggable}
            onDragStart={done || !draggable ? undefined : (event) => onDragStart?.(event, task)}
            onDragEnd={onDragEnd}
            // dragenter too: the drop indicator updates the moment the pointer enters a card (incl. the dragged
            // card itself, which clears it) instead of waiting for the next dragover tick.
            onDragEnter={(event) => onDragOver?.(event, task)}
            onDragOver={(event) => onDragOver?.(event, task)}
            onDrop={(event) => onDrop?.(event, task)}
            onClick={handleCardClick}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerEnd}
            style={swipeX ? { transform: `translateX(${swipeX}px)`, transition: swiping ? 'none' : undefined } : undefined}
            data-testid={`task-card-${task.id}`}
            data-status={status}
            data-done={done ? 'true' : undefined}
            data-selected={selected ? 'true' : undefined}
            aria-current={selected ? 'true' : undefined}
            className={cn(
              'group relative flex cursor-pointer flex-col gap-1.5 rounded-xl border border-line bg-surface px-3 py-2.5 shadow-card transition-[box-shadow,border-color,opacity,transform] max-sm:touch-pan-y',
              'hover:border-[color:var(--brand-soft-border)] hover:shadow-raised focus-within:shadow-raised',
              !done && draggable && 'active:cursor-grabbing',
              done && 'bg-surface-2 shadow-none',
              selected && 'border-[color:var(--brand-ink)] bg-brand-soft shadow-[inset_0_0_0_0.5px_var(--brand-ink)] hover:border-[color:var(--brand-ink)]',
              dragging && 'opacity-40',
              pillVisible && 'shadow-raised'
            )}
          >
            {/* 1 · status + title */}
            <div className="flex items-start gap-[7px]">
              <StatusIcon status={status} className="mt-[2px]" />
              <h3 className="min-w-0 flex-1 text-[13.5px] font-bold leading-[1.35]">
                <button
                  type="button"
                  onClick={() => onOpen(task)}
                  dir="auto"
                  className={cn(
                    'line-clamp-2 w-full rounded text-start text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    done && 'text-ink-3 line-through decoration-ink-4'
                  )}
                >
                  {task.name}
                </button>
              </h3>
            </div>
            {blocked && !done ? (
              <p className="-mt-0.5 pl-[22px] text-[12px] font-semibold leading-[1.35] text-status-red-ink">
                {task.blocked_reason ? `Blocked: ${task.blocked_reason}` : 'Blocked — no reason given yet'}
              </p>
            ) : null}

            {/* 2 · priority · due · attachments · location */}
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
              {done && status === 'Cancelled' ? (
                <StatusPill tone="outline" label="Cancelled" size="sm" />
              ) : done ? (
                <StatusPill tone="navy" label={task.completed_at ? `Done · ${formatDate(task.completed_at, { year: 'auto' })}` : 'Done'} size="sm" />
              ) : (
                <>
                  <PriorityFlag priority={task.priority} />
                  <DueBadge task={task} />
                </>
              )}
              {attachments ? (
                <span className="inline-flex items-center gap-[3px] font-semibold text-ink-3" title={`${attachments} attachment${attachments === 1 ? '' : 's'}`}>
                  <Paperclip className="h-3 w-3" aria-hidden="true" />{attachments}
                  <span className="sr-only"> attachment{attachments === 1 ? '' : 's'}</span>
                </span>
              ) : null}
              {location && !done ? (
                <span className="inline-flex min-w-0 items-center gap-[3px] font-semibold text-ink-3" title={(task.locations || []).join(', ')}>
                  <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
                  <span className="truncate">{location}</span>
                </span>
              ) : null}
            </div>

            {/* 3 · project · origin · people (+ hover actions) */}
            <div className="flex min-h-[22px] min-w-0 items-center gap-[5px]">
              {projects.length ? (
                <span
                  className="block h-5 min-w-0 shrink truncate rounded-md border border-line bg-surface-2 px-[7px] text-[11.5px] font-semibold leading-[18px] text-ink-2"
                  title={projects.map((project) => project.name).join(', ')}
                >
                  {projects[0].name}{projects.length > 1 ? ` +${projects.length - 1}` : ''}
                </span>
              ) : null}
              <OriginChip task={task} />
              <span className="ml-auto" />
              {shownAssignees.length ? (
                <Avatars assignees={shownAssignees} />
              ) : !done && readOnly ? (
                <span className="inline-flex shrink-0 items-center text-[11.5px] font-bold text-status-amber-ink">Unassigned</span>
              ) : !done ? (
                <button
                  type="button"
                  onClick={(event) => openPanel('assign', event)}
                  className="inline-flex shrink-0 items-center gap-1 rounded-full text-[11.5px] font-bold text-status-amber-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Unassigned — assign ${task.name}`}
                >
                  <span className="grid h-5 w-5 place-items-center rounded-full border-[1.5px] border-dashed border-status-amber">
                    <UserPlus className="h-3 w-3" aria-hidden="true" />
                  </span>
                  Unassigned
                </button>
              ) : null}

              {readOnly ? null : (
                <div
                  ref={pillRef}
                  className={cn(
                    'absolute bottom-2 right-2 z-[2] flex items-center gap-px rounded-[9px] border border-line bg-surface p-0.5 shadow-raised transition-opacity duration-150',
                    'pointer-events-none opacity-0 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100',
                    pillVisible && 'pointer-events-auto opacity-100',
                    '[@media(hover:none)]:pointer-events-auto [@media(hover:none)]:static [@media(hover:none)]:-my-1 [@media(hover:none)]:-mr-1.5 [@media(hover:none)]:border-0 [@media(hover:none)]:bg-transparent [@media(hover:none)]:p-0 [@media(hover:none)]:opacity-100 [@media(hover:none)]:shadow-none'
                  )}
                  data-testid={`task-actions-${task.id}`}
                >
                  {done ? (
                    <button
                      type="button"
                      onClick={() => onReopen(task)}
                      className={cn(actionButton, '[@media(hover:none)]:hidden')}
                      aria-label={`Reopen ${task.name}`}
                      title="Reopen"
                    >
                      <RotateCcw className="h-[15px] w-[15px]" aria-hidden="true" />
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => onDone(task)}
                        className={cn(actionButton, 'bg-status-green-bg text-status-green hover:bg-status-green-bg hover:text-status-green-ink [@media(hover:none)]:hidden')}
                        aria-label={`Mark ${task.name} done`}
                        title="Mark done"
                        data-testid={`task-done-btn-${task.id}`}
                      >
                        <CircleCheck className="h-[15px] w-[15px]" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={(event) => openPanel('due', event)}
                        className={cn(actionButton, panel === 'due' && 'bg-surface-hover text-ink', '[@media(hover:none)]:hidden')}
                        aria-label={`Set due date for ${task.name}`}
                        aria-expanded={panel === 'due'}
                        title="Due date"
                      >
                        <CalendarDays className="h-[15px] w-[15px]" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={(event) => openPanel('assign', event)}
                        className={cn(actionButton, panel === 'assign' && 'bg-surface-hover text-ink', '[@media(hover:none)]:hidden')}
                        aria-label={`Assign ${task.name}`}
                        aria-expanded={panel === 'assign'}
                        title="Assign"
                      >
                        <UserPlus className="h-[15px] w-[15px]" aria-hidden="true" />
                      </button>
                    </>
                  )}
                  <RowActions
                    label={`More actions for ${task.name}`}
                    items={menuItems}
                    onOpenChange={setMenuOpen}
                    // Touch: the only per-card action besides swiping — a 44px target whose negative margins keep
                    // the card row as high (and the ⋯ where it was) as with the 26px button.
                    className="h-[26px] w-[26px] rounded-md [@media(hover:none)]:-m-[9px] [@media(hover:none)]:h-11 [@media(hover:none)]:w-11 [@media(hover:none)]:rounded-xl"
                    data-testid={`task-menu-${task.id}`}
                  />
                </div>
              )}
            </div>
          </article>
        </PopoverAnchor>
      </div>
      {panel ? (
        <PopoverContent
          align="end"
          sideOffset={6}
          collisionPadding={12}
          className="w-[288px] rounded-xl border-line bg-surface p-0 pb-1 text-ink shadow-overlay"
          onClick={(event) => event.stopPropagation()}
          onInteractOutside={(event) => {
            // Clicking the same action button toggles the panel instead of closing + reopening it.
            if (pillRef.current?.contains(event.target)) event.preventDefault();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const target = returnFocusRef.current;
            if (target && document.contains(target)) target.focus();
          }}
        >
          {panel === 'due' ? (
            <DueDatePanel
              value={task.due_date}
              onPick={(value) => {
                setPanel(null);
                if ((task.due_date || '') === value) return;
                const payload = dueDatePatch(task, value);
                const single = payload.is_multi_day === false ? ' (now a single-day task)' : '';
                onPatch(task, payload, {
                  message: value ? `“${task.name}” due ${formatDate(value, { year: 'auto' })}${single}` : `Due date cleared on “${task.name}”${single}`,
                });
              }}
            />
          ) : null}
          {panel === 'assign' ? (
            <AssignPanel members={members} selectedIds={assigneeIds} onToggle={toggleAssignee} />
          ) : null}
          {panel === 'move' ? (
            <MovePanel
              sections={sections}
              current={sectionOf(task)}
              onPick={(section) => {
                setPanel(null);
                onMove(task, section);
              }}
            />
          ) : null}
        </PopoverContent>
      ) : null}
    </Popover>
  );
}
